import { CalendarRange } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { OfferedSlotsWeek } from './OfferedSlotsWeek';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  providerId: string;
  appointmentTypeId: string;
  locationId: string;
  /** Display label for the selected provider (for the caption). */
  providerLabel?: string;
  /** Display label for the selected appointment type (for the caption). */
  typeLabel?: string;
  /**
   * The appointment type's configured slot duration. Sizes the grid cells so a
   * 30-min type renders 30-min rows — applied even on empty weeks, where there
   * are no slots to infer the duration from.
   */
  durationMinutes?: number;
  /** Drives the same new-patient booking constraints the call would use. */
  isNewPatient: boolean;
}

/**
 * Preview of the appointment slots the scheduling agent would offer for the
 * selected provider + appointment type + location, on a navigable week grid
 * (`OfferedSlotsWeek`). The dialog unmounts its content on close, so each open
 * starts on the current week.
 */
export function OfferedSlotsDialog({
  open,
  onOpenChange,
  providerId,
  appointmentTypeId,
  locationId,
  providerLabel,
  typeLabel,
  durationMinutes,
  isNewPatient,
}: Props): React.ReactElement {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Cap the dialog at 85vh and let the grid body scroll, so a wide time
          range can't push the header / week-nav off-screen. */}
      <DialogContent className="flex max-h-[85vh] flex-col gap-0 p-0 !max-w-3xl">
        <div className="shrink-0 px-6 pt-6">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarRange className="h-4 w-4" />
              Offered appointment slots
            </DialogTitle>
            <DialogDescription>
              Times the agent could offer
              {providerLabel ? ` for ${providerLabel}` : ''}
              {typeLabel ? ` · ${typeLabel}` : ''}. Booked time, blocks, and booking limits are
              already applied.
            </DialogDescription>
          </DialogHeader>
        </div>
        <OfferedSlotsWeek
          providerId={providerId}
          appointmentTypeId={appointmentTypeId}
          locationId={locationId}
          durationMinutes={durationMinutes}
          isNewPatient={isNewPatient}
          enabled={open}
          navClassName="shrink-0 border-b px-6 pt-4 pb-4"
          bodyClassName="min-h-0 flex-1 overflow-y-auto px-6 py-4"
        />
      </DialogContent>
    </Dialog>
  );
}
